import { Button, Checkbox, PasswordInput, TextInput } from '@mantine/core';
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useRegisterForm } from 'src/Hooks/Forms/useRegisterForm';
import { useRegister } from 'src/Hooks/Mutations/useRegister';

import styles from './RegisterForm.module.scss';

// t('registerForm.titles.mr', 'Monsieur')
// t('registerForm.titles.ms', 'Madame')
// t('registerForm.titles.other', 'Autre')

const RegisterForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') ?? undefined;
  const form = useRegisterForm(initialEmail);

  const navigate = useNavigate();
  const { mutate, isLoading } = useRegister({ onSuccess: () => navigate('/auth/verify-email') });

  return (
    <div className={styles.container}>
      <form onSubmit={form.onSubmit(values => mutate(values))} className={styles.form}>
        <div className={styles.subcontainer}>
          <div>
            <p className={styles.title}>
              {t('registerForm.welcomeTitle', "L'aventure commence ici 🚀")}
            </p>
            <p className={styles.subtitle}>
              {t(
                'registerForm.welcomeSubtitle',
                'Simplifiez la gestion de vos biens en toute convivialité.'
              )}
            </p>
          </div>
          <div className={styles.formLine}>
            <TextInput
              placeholder={t('registerForm.typeFirstName', 'Entrez votre prénom')}
              description={t('registerForm.firstNameDescription', 'Prénom')}
              {...form.getInputProps('firstName')}
            />
            <TextInput
              placeholder={t('registerForm.typeLastName', 'Entrez votre nom')}
              description={t('registerForm.lastNameDescription', 'Nom')}
              {...form.getInputProps('lastName')}
            />
          </div>
          <TextInput
            placeholder={t('registerForm.typeEmail', 'Entrez votre adresse email')}
            description={t('registerForm.emailDescription', 'Email')}
            {...form.getInputProps('email')}
          />
          <div className={styles.formLine}>
            <PasswordInput
              placeholder="············"
              description={t('registerForm.passwordDescription', 'Mot de passe')}
              {...form.getInputProps('password')}
            />
            <PasswordInput
              placeholder="············"
              description={t('registerForm.passwordConfirmDescription', 'Confirmation')}
              {...form.getInputProps('confirmPassword')}
            />
          </div>
          <p className={styles.passwordHint}>
            {t(
              'registerForm.passwordHint',
              'Min. 8 caractères, 1 majuscule, 1 minuscule et 1 chiffre'
            )}
          </p>
          <Checkbox
            label={
              <div className={styles.checkboxLabel}>
                {t(
                  'registerForm.newsletterOptIn',
                  'Je souhaite recevoir des informations et offres commerciales par e-mail.'
                )}
              </div>
            }
            {...form.getInputProps('newsletterOptIn', { type: 'checkbox' })}
          />
          <p className={styles.legalText}>
            <Trans i18nKey="registerForm.legalNotice">
              En vous inscrivant, vous acceptez nos{' '}
              <a
                href="https://www.shareheritage.com/hubfs/Site%20web%20-%20footer/CGU%20Juin%2025.pdf?hsLang=fr-fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                Conditions générales d&apos;utilisation (CGU)
              </a>{' '}
              et{' '}
              <a
                href="https://www.shareheritage.com/hubfs/Site%20web%20-%20footer/CGS%20SH%20Juin%2025.pdf?hsLang=fr-fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                services (CGS)
              </a>{' '}
              et notre{' '}
              <a
                href="https://www.shareheritage.com/hubfs/Site%20web%20-%20PDF/Politique%20de%20Confidentialit%C3%A9%20Juin%2025.pdf?hsLang=fr-fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                Politique de confidentialité
              </a>
              . Les informations collectées par Share Heritage SA sont traitées dans le but de créer
              votre compte, gérer votre session, l&apos;utilisation et l&apos;amélioration de nos
              services et pour assurer la sécurité de notre plateforme. Vous avez des droits
              concernant vos données personnelles et vous pouvez les exercer en contactant Share
              Heritage par le biais de notre{' '}
              <a
                href="https://www.shareheritage.com/contact-shareheritage"
                target="_blank"
                rel="noopener noreferrer"
              >
                Formulaire de contact
              </a>
              . Vous pouvez en apprendre davantage sur vos droits et la manière dont nous traitons
              vos données personnelles dans notre{' '}
              <a
                href="https://www.shareheritage.com/hubfs/Site%20web%20-%20PDF/Engagements%20RGPD%20Juin%2025.pdf?hsLang=fr-fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                Engagement de conformité au RGPD
              </a>
              .
            </Trans>
          </p>
          <p className={styles.legalText}>
            <Trans i18nKey="registerForm.cookieNotice">
              Vous pouvez consulter notre{' '}
              <a
                href="https://www.shareheritage.com/hubfs/Site%20web%20-%20PDF/Politique%20Cookies%20Juin%2025.pdf?hsLang=fr-fr"
                target="_blank"
                rel="noopener noreferrer"
              >
                politique de cookies
              </a>{' '}
              à tout moment.
            </Trans>
          </p>
          <Button variant="filled" type="submit" loading={isLoading} disabled={isLoading}>
            {t('registerForm.register', "S'inscrire")}
          </Button>
          <p className={styles.securityText}>
            {t(
              'registerForm.securityNotice',
              '🔒 Connexion sécurisée SSL. Vos informations restent privées.'
            )}
          </p>
        </div>
      </form>
    </div>
  );
};

export default RegisterForm;
